/** COMPANY ORGANISATIONS (plan 81, stages 2 and 2b; Tom, 2026-10-30;
 *  VC-Authz's design note v3.2).
 *
 *  - An organisation made by a company's sign-in (a Microsoft tenant, a
 *    Google Workspace) has no owner until an administrator of that company
 *    signs in, or SourcePilot staff make one: until then it is
 *    "unverified", and nobody in it can bring in outsiders.
 *  - Membership gained through the company's sign-in counts only in a
 *    session signed in through that company: signed in another way, the
 *    organisation shows ("Contoso — sign in with Microsoft to open") but
 *    nothing in it opens.
 *  - Members send colleagues a sign-in link (an email, no grant), at the
 *    company's verified domains only; admins and owners of a verified one
 *    invite as today.
 *
 *  A refusal is thrown as an ApiError with the server's own words, for
 *  `toRefusal`. */

import { ApiError } from "./api-error";
import { API_BASE_URL } from "./hooks/use-neo-client";

export type CompanyProvider = "microsoft" | "google";

export type OrganisationCompany = {
  /** null: an organisation made by invitation (no company sign-in behind it) */
  provider: CompanyProvider | null;
  /** the company's verified domains; [] when never read */
  domains: string[];
  /** it has an owner */
  verified: boolean;
  verified_by: { name: string | null; how: "microsoft_admin" | "google_admin" | "sourcepilot" } | null;
  me: {
    /** may send a colleague a sign-in link */
    send_signin_links: boolean;
    /** the levels a real invitation of mine may grant ([] for a member) */
    invite_levels: string[];
    /** I am one of its owners */
    owner: boolean;
  };
  /** how the members the reader sees joined, by user id: only those who
   *  joined through the company's sign-in */
  joined?: Record<string, "microsoft_sign_in" | "google_sign_in">;
  /** the members the reader sees who did NOT join through the company's
   *  sign-in (added directly, or by invitation): user ids. A member in
   *  neither has no line — never read external from a missing entry. */
  external?: string[];
  /** Google only: whether colleagues join now */
  joining?: CompanyJoining;
};

/** Why a Google Workspace organisation takes no newcomers now: the daily
 *  directory check failed ("check_failed": none succeeded for 3 days). */
export type JoiningPausedReason =
  | "not_verified"
  | "grant_ended"
  | "admin_lost"
  | "customer_changed"
  | "domains_gone"
  | "check_failed";

/** A GOOGLE WORKSPACE ORGANISATION'S JOINING (Tom, 2026-10-31). Unbound
 *  (no administrator has verified it), anyone from its hosted domain joins:
 *  the unverified banner. Bound, colleagues join while the daily directory
 *  check passes; a failed one pauses joining, and the owners are asked to
 *  have an administrator verify again (`reconnect`). */
export type CompanyJoining = {
  open: boolean;
  /** an administrator's verify bound it to the Workspace */
  bound: boolean;
  /** the last check that succeeded */
  checked_at: string | null;
  paused_reason: JoiningPausedReason | null;
  reconnect: boolean;
};

/** Why joining is paused, as the owners read it. */
export function pausedReasonWords(r: JoiningPausedReason | null): string {
  switch (r) {
    case "grant_ended":
      return "The administrator's access for SourcePilot was removed or has expired.";
    case "admin_lost":
      return "The administrator who verified it is no longer a Google Workspace administrator.";
    case "customer_changed":
      // the stored grant now answers for another Workspace customer
      return "The administrator who verified it is now in a different Google Workspace.";
    case "domains_gone":
      return "The Google Workspace no longer lists any of the organisation's domains.";
    case "not_verified":
      return "No administrator has verified it.";
    default:
      return "SourcePilot couldn't check the Google Workspace for 3 days.";
  }
}

/** "Microsoft", "Google" */
export function providerWords(p: CompanyProvider): string {
  return p === "microsoft" ? "Microsoft" : "Google";
}

/** the sign-in route's provider id */
const PROVIDER_ID: Record<CompanyProvider, string> = { microsoft: "azure-ad", google: "google" };

/** A same-origin path, or null: never a scheme, a host, "//" or a
 *  backslash (an open redirect otherwise). */
export function safeReturnPath(p: string | null | undefined): string | null {
  if (typeof p !== "string" || !p.startsWith("/") || p.startsWith("//")) return null;
  if (/[\\\s]/.test(p) || /^\/[^/?#]*:/.test(p)) return null;
  return p;
}

/** Where a company sign-in starts: the server redirects to Microsoft or
 *  Google, and lands back on `returnTo` (a same-origin path) when given. */
export function companySignInUrl(
  provider: CompanyProvider,
  opts: { loginHint?: string | null; returnTo?: string | null; intent?: "verify" } = {},
): string {
  const q = new URLSearchParams();
  if (opts.loginHint) q.set("login_hint", opts.loginHint);
  const back = safeReturnPath(opts.returnTo);
  if (back) q.set("return_to", back);
  if (opts.intent) q.set("intent", opts.intent);
  const qs = q.toString();
  return `${API_BASE_URL}/auth/oauth/${PROVIDER_ID[provider]}${qs ? `?${qs}` : ""}`;
}

/** The verify page for that provider's administrator, as a member
 *  forwards it: no organisation id (their own sign-in says which). */
export function verifyPath(provider: CompanyProvider): string {
  return provider === "google" ? "/verify-organisation?provider=google" : "/verify-organisation";
}

/** The address's domain is exactly one of `domains` (lower case). The
 *  server decides; this only says so before sending. */
export function atDomains(email: string, domains: string[]): boolean {
  const at = email.trim().toLowerCase().lastIndexOf("@");
  if (at < 1) return false;
  const domain = email.trim().toLowerCase().slice(at + 1);
  return domains.some((d) => d.toLowerCase() === domain);
}

/** The organisation's company state, as one of its members reads it; null
 *  for anyone else (a 404, alike for a missing organisation). */
export async function readOrganisationCompany(orgId: string): Promise<OrganisationCompany | null> {
  const res = await fetch(`${API_BASE_URL}/organisations/${encodeURIComponent(orgId)}/company`, {
    credentials: "include",
    cache: "no-store",
  });
  if (res.status === 404) return null;
  const text = await res.text();
  let parsed: unknown = null;
  try { parsed = text ? JSON.parse(text) : null; } catch { parsed = text; }
  if (!res.ok) throw new ApiError(res.status, parsed, text.slice(0, 300));
  return parsed as OrganisationCompany;
}

/** Email a colleague a sign-in link to the organisation's company sign-in:
 *  no invitation, no grant; they join by signing in from that company. */
export async function sendSigninLink(orgId: string, email: string): Promise<{ status: string }> {
  const res = await fetch(`${API_BASE_URL}/organisations/${encodeURIComponent(orgId)}/signin-links`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  const text = await res.text();
  let parsed: unknown = null;
  try { parsed = text ? JSON.parse(text) : null; } catch { parsed = text; }
  if (!res.ok) throw new ApiError(res.status, parsed, text.slice(0, 300));
  return parsed as { status: string };
}

/** The one-off word after a company sign-in (on /auth/user, until
 *  consumed): "now_owner" after any sign-in that made the person owner
 *  (`domain_moved`: the domain had been another Google Workspace's, whose
 *  organisation stays theirs; this one is new); "no_admin_role" only after
 *  the verify page's sign-in (about no organisation while a Google one's
 *  joining is paused); "no_company" after the verify page's sign-in with an
 *  account in no company's directory (a personal account, a guest);
 *  "joining_paused" after a Google sign-in that landed in the personal
 *  space because the company's organisation takes no newcomers now. */
export type SignInNotice =
  | {
      /** a company administrator's sign-in into an organisation with no
       *  owner: they choose one (Tom, 2026-10-31) */
      kind: "choose_owner";
      organisation: { id: string; name: string | null };
      provider?: CompanyProvider;
      domain_moved?: boolean;
    }
  | {
      kind: "now_owner";
      organisation: { id: string; name: string | null };
      provider?: CompanyProvider;
      domain_moved?: boolean;
    }
  | {
      kind: "no_admin_role";
      organisation: { id: string; name: string | null } | null;
      /** whose administrator role it was about (Microsoft when not said) */
      provider?: CompanyProvider;
    }
  | { kind: "no_company"; provider?: CompanyProvider }
  | { kind: "joining_paused"; provider?: CompanyProvider };

export function signInNoticeOf(user: unknown): SignInNotice | null {
  const n = (user as { notice?: unknown } | null)?.notice as
    | { kind?: unknown; organisation?: { id?: unknown; name?: string | null } | null; provider?: unknown; domain_moved?: unknown }
    | null
    | undefined;
  if (!n) return null;
  const provider: CompanyProvider = n.provider === "google" ? "google" : "microsoft";
  if (n.kind === "no_company") return { kind: "no_company", provider };
  if (n.kind === "joining_paused") return { kind: "joining_paused", provider };
  const org = typeof n.organisation?.id === "string" ? { id: n.organisation.id, name: n.organisation.name ?? null } : null;
  if (n.kind === "no_admin_role" && (org || n.organisation === null)) return { kind: n.kind, organisation: org, provider };
  if ((n.kind !== "now_owner" && n.kind !== "choose_owner") || !org) return null;
  return n.domain_moved === true
    ? { kind: n.kind, organisation: org, provider, domain_moved: true }
    : { kind: n.kind, organisation: org, provider };
}

/** The organisation a notice is about, if any. */
export function noticeOrganisation(n: SignInNotice): { id: string; name: string | null } | null {
  return n.kind === "now_owner" || n.kind === "choose_owner" || n.kind === "no_admin_role" ? n.organisation : null;
}

/** OWNERS AND THE DIRECTORY (plan 81 stages 3 and 5; Tom, 2026-10-31;
 *  routes/directory_routes.py). No owner is made silently: a company
 *  administrator (Microsoft: Global or Privileged Role Administrator;
 *  Google: the verify page's sign-in) chooses one, and for an hour after
 *  that sign-in may add owners and bind directory groups. Refusals are
 *  ApiErrors with the server's words (403 `company_admin_required`: the
 *  administrator's sign-in, again after the hour). */

async function companyJson<T>(path: string, init?: RequestInit): Promise<T | null> {
  const res = await fetch(`${API_BASE_URL}${path}`, { credentials: "include", cache: "no-store", ...init });
  if (res.status === 404 && !init?.method) return null;
  const text = await res.text();
  let parsed: unknown = null;
  try { parsed = text ? JSON.parse(text) : null; } catch { parsed = text; }
  if (!res.ok) throw new ApiError(res.status, parsed, text.slice(0, 300));
  return parsed as T;
}

const jsonBody = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

export type OwnerChoices = {
  has_owner: boolean;
  /** the company's directory is connected (Microsoft admin consent) */
  connected: boolean;
  members: { id: string; name: string | null; me: boolean }[];
  /** directory groups (Microsoft only); `admin_role`: it holds Global or
   *  Privileged Role Administrator (known only when connected) */
  groups: { id: string; name: string | null; admin_role: boolean }[];
};

/** Who a company administrator may make owner; null when not a member (404). */
export async function readOwnerChoices(orgId: string): Promise<OwnerChoices | null> {
  const r = await companyJson<OwnerChoices>(`/organisations/${encodeURIComponent(orgId)}/owner-choices`);
  return r && Array.isArray(r.members) && Array.isArray(r.groups) ? r : null;
}

export type OwnerChoice = { self: true } | { user_id: string } | { group_id: string };

export async function chooseOwner(orgId: string, choice: OwnerChoice): Promise<{ status: "owner" | "already_owner"; verified: boolean }> {
  return (await companyJson(`/organisations/${encodeURIComponent(orgId)}/owners/choose`, jsonBody("POST", choice)))!;
}

/** Take a directory group out of Owners (an owner in a current
 *  administrator sign-in; refused for the last owner). */
export async function removeOwnerGroup(orgId: string, groupId: string): Promise<{ status: "removed" | "not_owner" }> {
  return (await companyJson(`/organisations/${encodeURIComponent(orgId)}/owners/remove`, jsonBody("POST", { group_id: groupId })))!;
}

/** Where the company administrator connects its directory (Microsoft's
 *  admin consent); back at /organisations/{id}?directory=connected|not_connected. */
export function directoryConnectUrl(orgId: string): string {
  return `${API_BASE_URL}/organisations/${encodeURIComponent(orgId)}/directory/connect`;
}

export type BindingScopeKind = "organisation" | "workspace" | "project";

/** Where a binding is: says itself, so a project's binding reads without
 *  its workspace in any list (`scope_name` null: not readable). */
export type DirectoryBinding = {
  role_group: string;
  role: string;
  scope_id: string | null;
  scope_kind?: BindingScopeKind;
  scope_name?: string | null;
  parent_id?: string | null;
  by?: string;
  at?: string;
};

/** A scope an owner may bind a directory group at (finding 6; Tom,
 *  2026-11-01): the organisation, one of its workspaces, or a project
 *  (parent_id: its workspace). The server lists them; nothing is guessed. */
export type BindingTarget = {
  scope_id: string;
  kind: BindingScopeKind;
  name: string | null;
  parent_id: string | null;
  roles: string[];
};

export type Directory = {
  connected: boolean;
  connected_at: string | null;
  /** someone's groups couldn't be read: the directory isn't connected */
  unread_at: string | null;
  /** connected, and a sign-in's group read failed then (cleared by the next
   *  good read); memberships stay as they were meanwhile */
  read_failed_at?: string | null;
  read_failed_reason?: "consent_revoked" | "permission_missing" | "graph_error" | null;
  groups: {
    id: string;
    name: string | null;
    members: number;
    bindings: DirectoryBinding[];
    /** the tenant's id for the group */
    object_id?: string;
    /** "chosen": chosen in SourcePilot (a tenant that can't assign groups
     *  to the app); "assigned": it came with people's sign-ins */
    source?: "assigned" | "chosen";
  }[];
  /** the organisation first, then its workspaces by name (at most 200);
   *  projects only through searchBindingTargets */
  targets?: BindingTarget[];
  /** more workspaces than listed: found by searching */
  targets_more?: boolean;
};

/** The access review, for owners and admins of a Microsoft company
 *  organisation; null for anyone else (404). */
export async function readDirectory(orgId: string): Promise<Directory | null> {
  const r = await companyJson<Directory>(`/organisations/${encodeURIComponent(orgId)}/directory`);
  return r && Array.isArray(r.groups) ? r : null;
}

export type BindableRole = "member" | "admin";

/** A group in the company's Microsoft directory, read live from it. */
export type TenantGroup = { object_id: string; name: string | null; chosen: boolean };

/** One page of the tenant's groups by name prefix (Tom, 2026-11-02:
 *  directory groups on tenants that can't assign groups to the app). */
export async function readTenantGroups(orgId: string, find: { q?: string; after?: string }): Promise<{ groups: TenantGroup[]; next: string | null }> {
  const params = new URLSearchParams();
  if (find.q) params.set("q", find.q);
  if (find.after) params.set("after", find.after);
  const qs = params.toString();
  const r = await companyJson<{ groups: TenantGroup[]; next: string | null }>(
    `/organisations/${encodeURIComponent(orgId)}/directory/tenant-groups${qs ? `?${qs}` : ""}`,
  );
  return r && Array.isArray(r.groups) ? { groups: r.groups, next: r.next ?? null } : { groups: [], next: null };
}

/** Choose a tenant group in SourcePilot, or unchoose it (refused while it
 *  is bound anywhere: `still_bound`, with its bindings). */
export async function chooseTenantGroup(orgId: string, objectId: string, chosen: boolean): Promise<{ status: string; group_id: string }> {
  return (await companyJson(
    `/organisations/${encodeURIComponent(orgId)}/directory/chosen-groups`,
    jsonBody(chosen ? "POST" : "DELETE", { object_id: objectId }),
  ))!;
}

/** `still_bound`: the bindings the server named (same shape as the
 *  directory read's); null for any other refusal. */
export function stillBoundBindings(e: unknown): DirectoryBinding[] | null {
  if (!(e instanceof ApiError)) return null;
  const d = e.detail as { code?: unknown; bindings?: unknown } | null;
  return d && typeof d === "object" && d.code === "still_bound" && Array.isArray(d.bindings) ? (d.bindings as DirectoryBinding[]) : null;
}

/** One page of binding targets, by name prefix (`q`), within a workspace
 *  for projects; `next` continues it. */
export async function searchBindingTargets(
  orgId: string,
  find: { kind: "project" | "workspace"; workspace_id?: string; q?: string; after?: string; limit?: number },
): Promise<{ targets: BindingTarget[]; next: string | null }> {
  const params = new URLSearchParams({ kind: find.kind });
  if (find.workspace_id) params.set("workspace_id", find.workspace_id);
  if (find.q) params.set("q", find.q);
  if (find.after) params.set("after", find.after);
  if (find.limit) params.set("limit", String(find.limit));
  const r = await companyJson<{ targets: BindingTarget[]; next: string | null }>(
    `/organisations/${encodeURIComponent(orgId)}/directory/targets?${params}`,
  );
  return r && Array.isArray(r.targets) ? { targets: r.targets, next: r.next ?? null } : { targets: [], next: null };
}

export async function bindDirectoryGroup(orgId: string, groupId: string, scopeId: string, role: BindableRole): Promise<{ status: string }> {
  return (await companyJson(
    `/organisations/${encodeURIComponent(orgId)}/directory-groups/${encodeURIComponent(groupId)}/bindings`,
    jsonBody("POST", { scope_id: scopeId, role }),
  ))!;
}

export async function unbindDirectoryGroup(orgId: string, groupId: string, scopeId: string, role: BindableRole): Promise<{ status: string }> {
  return (await companyJson(
    `/organisations/${encodeURIComponent(orgId)}/directory-groups/${encodeURIComponent(groupId)}/bindings`,
    jsonBody("DELETE", { scope_id: scopeId, role }),
  ))!;
}

/** Clear the notice once it has been shown (reading /auth/user never does).
 *  A JSON body, as the server takes no other (a cross-site form can't). */
export async function consumeSignInNotice(): Promise<void> {
  await fetch(`${API_BASE_URL}/auth/notice/consume`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: "{}",
  }).catch(() => undefined);
}
